import { typography } from '../../../theme/tokens/typography';

/** Profile list table — visual overrides passed into shared DataTable slots. */
export function createProfileTableOverrides({ semantic, accent } = {}) {
  const table = semantic?.table ?? {};
  const borders = semantic?.borders ?? {};
  const text = semantic?.text ?? {};
  const borderColor = table.borderColor ?? borders.border ?? '#E4E7EC';
  const headerBg = table.headerBackground ?? '#F8FAFC';
  const rowHover = table.rowHoverBackground ?? '#F8FAFC';
  const rowBg = table.rowBackground ?? semantic?.surfaces?.surface ?? '#FFFFFF';
  const cellPaddingX = table.cellPaddingX ?? 20;

  return {
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: `${headerBg} !important`,
        color: `${text.muted ?? '#64748B'} !important`,
        fontSize: typography.fontSizeXs,
        fontWeight: typography.fontWeightSemibold,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        borderBottom: `1px solid ${borderColor}`,
        py: 1.75,
        px: `${cellPaddingX}px`,
        height: 48,
        whiteSpace: 'nowrap',
        '& .Mui-TableHeadCell-Content': {
          color: `${text.muted ?? '#64748B'} !important`,
        },
        '& .Mui-TableHeadCell-Content-Labels': {
          color: `${text.muted ?? '#64748B'} !important`,
        },
        '& .MuiTypography-root': {
          fontSize: `${typography.fontSizeXs} !important`,
          fontWeight: `${typography.fontWeightSemibold} !important`,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: `${text.muted ?? '#64748B'} !important`,
        },
        '&:first-of-type': {
          pl: `${cellPaddingX + 4}px`,
        },
        '&:last-of-type': {
          pr: `${cellPaddingX + 4}px`,
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: typography.fontSizeMd,
        color: text.primary,
        backgroundColor: rowBg,
        borderBottom: `1px solid ${borderColor}`,
        py: 0,
        px: `${cellPaddingX}px`,
        height: 52,
        verticalAlign: 'middle',
        '&:first-of-type': {
          pl: `${cellPaddingX + 4}px`,
        },
        '&:last-of-type': {
          pr: `${cellPaddingX + 4}px`,
        },
      },
    },
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: rowBg,
        transition: 'background-color 120ms ease',
        '&:hover td': {
          backgroundColor: `${rowHover} !important`,
        },
        '&:last-child td': {
          borderBottom: 'none',
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        borderTop: `1px solid ${borderColor}`,
      },
    },
    tableLayout: {
      '& [data-testid="table-layout-surface"]': {
        borderColor,
        boxShadow:
          table.surfaceShadow ??
          '0 1px 3px rgba(16, 24, 40, 0.08), 0 1px 2px rgba(16, 24, 40, 0.04)',
      },
    },
    accentPrimary: accent?.primary,
  };
}

/** Toolbar filters — outside table card, search + org on one row. */
export function createProfileTableFilters({ semantic } = {}) {
  const borders = semantic?.borders ?? {};
  const borderColor = borders.border ?? '#E4E7EC';
  const surface = semantic?.surfaces?.surface ?? '#FFFFFF';

  return {
    root: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: { xs: 'wrap', md: 'nowrap' },
      gap: 1.5,
      width: '100%',
    },
    search: {
      width: { xs: '100%', sm: 300 },
      maxWidth: { md: 340 },
      flexShrink: 0,
      '& > div:last-of-type': {
        height:'40px',
        bgcolor: surface,
        borderColor,
        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
        transition: 'border-color 140ms ease, box-shadow 140ms ease',
        '&:focus-within': {
          boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.08)',
        },
      },
    },
    orgFilter: {
      width: { xs: '100%', sm: 280 },
      maxWidth: { md: 300 },
      flexShrink: 0,
      '& .MuiAutocomplete-root': {
        width: '100%',
      },
      '& .MuiOutlinedInput-root': {
        bgcolor: surface,
        borderRadius: '8px',
        fontSize: typography.fontSizeSm,
        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
        transition: 'border-color 140ms ease, box-shadow 140ms ease',
        '& fieldset': {
          borderColor,
        },
        '&:hover fieldset': {
          borderColor: '#CBD5E1',
        },
        '&.Mui-focused fieldset': {
          borderColor: semantic?.interactive?.primary ?? '#2563EB',
          borderWidth: '1px',
        },
        '&.Mui-focused': {
          boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.08)',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
                      top:0,
                    },
    },
  };
}
